import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import Seo from "@/components/Seo";
import FAQ from "@/components/FAQ";
import { useLang } from "@/context/LanguageContext";
import { blogs } from "@/data/blogs";
import { blogFaqs } from "@/data/faqs";
import { faqJsonLd } from "@/lib/seo";
import { fetchPublicBlogs } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const normalizeBlog = (b) => ({
    ...b,
    readTime: b.readTime || b.read_time || "5 min read",
    image: b.image && b.image.startsWith("/api/") ? `${BACKEND}${b.image}` : (b.image || "/blog/clarity-before-marketing.webp"),
    alt: b.alt || `${b.title} — ${b.category} article by Abhijit JK`,
});

export function useAllBlogs() {
    const [dynamicBlogs, setDynamicBlogs] = useState([]);
    useEffect(() => {
        fetchPublicBlogs()
            .then((docs) => setDynamicBlogs(docs.map(normalizeBlog)))
            .catch(() => {});
    }, []);
    const staticSlugs = new Set(blogs.map((b) => b.slug));
    return [...dynamicBlogs.filter((b) => !staticSlugs.has(b.slug)), ...blogs.map(normalizeBlog)];
}

export default function Blog() {
    const { t } = useLang();
    const allBlogs = useAllBlogs();
    return (
        <div data-testid="blog-page" className="relative">
            <Seo
                title="Blog · Branding, Business Growth & Brand Strategy — Abhijit JK"
                description="Practical, jargon-free articles on branding, business growth, marketing, websites, AI tools and personal branding by Abhijit JK, Founder of Ragi Media, Bengaluru."
                keywords="business strategy blog, branding tips, how to grow a small business, marketing for founders, personal branding, Abhijit JK blog, Ragi Media"
                path="/blog"
                jsonLd={faqJsonLd(blogFaqs)}
            />
            <section className="mx-auto max-w-7xl px-6 pt-20 pb-10 lg:px-10">
                <Reveal>
                    <SectionEyebrow>{t("blog_eyebrow")}</SectionEyebrow>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
                        {t("blog_pre")}{" "}
                        <span className="gold-text">{t("blog_gold")}</span>
                    </h1>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base text-white/65 md:text-lg">
                        {t("blog_sub")}
                    </p>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {allBlogs.map((b, i) => (
                        <Reveal key={b.slug} delay={(i % 3) * 0.08}>
                            <Link
                                to={`/blog/${b.slug}`}
                                data-testid={`blog-card-${b.slug}`}
                                className="group block h-full"
                            >
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    transition={{ type: "spring", stiffness: 220, damping: 18 }}
                                    className="glass glow-border flex h-full flex-col overflow-hidden rounded-lg"
                                >
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img
                                            src={b.image}
                                            alt={b.alt || `${b.title} — ${b.category} article by Abhijit JK`}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col p-7">
                                        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-gold-300/80">
                                            <span>{b.category}</span>
                                            <span className="text-white/30">·</span>
                                            <span className="text-white/40">{b.readTime}</span>
                                        </div>
                                        <h2 className="mt-4 font-display text-xl font-medium leading-snug">
                                            {b.title}
                                        </h2>
                                        <p className="mt-3 flex-1 text-sm leading-relaxed text-white/60">
                                            {b.excerpt}
                                        </p>
                                        <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold-300">
                                            Read More <ArrowRight size={12} />
                                        </span>
                                    </div>
                                </motion.div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            <FAQ items={blogFaqs} eyebrow="FAQ" title="About the" gold="blog." />
        </div>
    );
}
